import { DepartureDto, LegDto, QuayDto, PointsOnLinkDto } from '@/types/webapi';
import type { Feature, LineString, Point } from 'geojson';
import { isEmpty } from 'lodash-es';

// Ref: https://jsfiddle.net/welesley/tw7qLvh4/2/

/**
 * Decodes to a [latitude, longitude] coordinates array.
 *
 * This is adapted from the implementation in Project-OSRM.
 *
 * @param {String} str
 * @param {Number} precision
 * @returns {Array}
 *
 * @see https://github.com/Project-OSRM/osrm-frontend/blob/master/WebContent/routing/OSRM.RoutingGeometry.js
 */
function _decode(str: string, precision: number = 5): number[][] {
    if (!str) return [];

    let index = 0,
        lat = 0,
        lng = 0,
        coordinates = [],
        shift = 0,
        result = 0,
        byte = null,
        latitude_change,
        longitude_change,
        factor = Math.pow(10, precision || 5);

    // Coordinates have variable length when encoded, so just keep
    // track of whether we've hit the end of the string. In each
    // loop iteration, a single coordinate is decoded.
    while (index < str.length) {
        // Reset shift, result, and byte
        byte = null;
        shift = 0;
        result = 0;

        do {
            byte = str.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        latitude_change = result & 1 ? ~(result >> 1) : result >> 1;

        shift = result = 0;

        do {
            byte = str.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        longitude_change = result & 1 ? ~(result >> 1) : result >> 1;

        lat += latitude_change;
        lng += longitude_change;

        const coordinate = [lat / factor, lng / factor];

        // if last one is the same as the current one, skip
        if (
            coordinates.length > 0 &&
            coordinates[coordinates.length - 1][0] === coordinate[0] &&
            coordinates[coordinates.length - 1][1] === coordinate[1]
        ) {
            continue;
        }

        coordinates.push(coordinate);
    }

    return coordinates;
}

/**
 * Encodes a [latitude, longitude] coordinates array to a polyline string.
 *
 * This is adapted from the implementation in Project-OSRM.
 *
 * @param {Array} coordinates
 * @param {Number} precision
 * @returns {String}
 *
 * @see https://github.com/Project-OSRM/osrm-frontend/blob/master/WebContent/routing/OSRM.RoutingGeometry.js
 */
export function encode(coordinates: number[][], precision: number = 5): string {
    if (!coordinates.length) return '';

    const output: string[] = [];
    let prevLat = 0;
    let prevLng = 0;
    const factor = Math.pow(10, precision);

    const encodeValue = (value: number) => {
        // ZigZag encode signed integers to unsigned
        let val = (value << 1) ^ (value >> 31);
        do {
            let byte = val & 0x1f;
            val = val >> 5;
            if (val !== 0) {
                byte |= 0x20;
            }
            output.push(String.fromCharCode(byte + 63));
        } while (val !== 0);
    };

    for (const [lat, lng] of coordinates) {
        const currentLat = Math.round(lat * factor);
        const currentLng = Math.round(lng * factor);

        // Calculate deltas from previous coordinates
        encodeValue(currentLat - prevLat);
        encodeValue(currentLng - prevLng);

        prevLat = currentLat;
        prevLng = currentLng;
    }

    return output.join('');
}

/**
 * Decodes to a GeoJSON LineString geometry.
 *
 * @param {String} str
 * @param {Number} precision
 * @returns {LineString}
 */
export function toLineString(str: string, precision: number = 5): LineString {
    var coords = _decode(str, precision);
    return {
        type: 'LineString',
        coordinates: coords.reduce<number[][]>((acc, curr) => {
            acc.push(curr.slice().reverse());

            return acc;
        }, [])
    };
}

/**
 * Decodes all given point links to a GeoJSON LineString geometry.
 *
 * @param {PointsOnLinkDto[]} point links
 * @param {Number} precision
 * @returns {LineString}
 */
export function pointsToLineString(points: PointsOnLinkDto[], precision: number = 5): LineString {
    const lines = points.reduce<LineString[]>((acc, curr) => {
        acc.push(toLineString(curr.points, precision));
        return acc;
    }, []);

    return {
        type: 'LineString',
        coordinates: lines
            .flatMap(l => l.coordinates)
            .filter((c: number[], i: number, arr: number[][]) => i === 0 || c[0] !== arr[i - 1][0] || c[1] !== arr[i - 1][1])
    };
}

export type LegLineStringProperties = LegDto & { [key: string]: any };

export type DepartureLineStringProperties = DepartureDto & { [key: string]: any };

export type QuayPointProperties = Omit<QuayDto, 'coordinates'> & { [key: string]: any };

export function useGeojsonHelper() {
    function mapQuayToPoint(quay: QuayDto): Feature<Point, QuayPointProperties> | null {
        if (!quay || isEmpty(quay?.coordinates)) return null;

        const {
            coordinates: { latitude, longitude },
            ...properties
        } = quay;

        return {
            id: properties.id,
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [longitude, latitude]
            },
            properties
        };
    }

    return {
        mapQuayToPoint,
        toLineString
    };
}
